import { useEffect, useState } from "react";

/*
 * Restrained commit confirmation. Sits above the tapped commit control
 * as a quiet nod: a short message pill + a small localized ring of
 * sage/gold dots that fade in ~900ms. Never blocks input.
 *
 * If the user prefers reduced motion, we show only the message pill and
 * skip the particle animation entirely.
 */
function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function Celebration({ visible, message }) {
  const [particles, setParticles] = useState([]);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    setReduced(prefersReducedMotion());
  }, []);

  useEffect(() => {
    if (!visible) { setParticles([]); return; }
    if (reduced) { setParticles([]); return; }

    // Small, calm ring — 10 particles, tight radius (~40–70px)
    const seeds = Array.from({ length: 10 }, (_, i) => {
      const angle = (i / 10) * Math.PI * 2 + (Math.random() * 0.25 - 0.125);
      const dist = 40 + Math.random() * 30;
      const palette = ["#B08A50", "#6B8065", "#C4A76A"]; // commit-gold / sage / warm-tertiary
      return {
        id: `${Date.now()}-${i}`,
        dx: Math.cos(angle) * dist,
        dy: Math.sin(angle) * dist - 12,
        size: 4 + Math.random() * 3,
        color: palette[i % palette.length],
        delay: Math.random() * 70,
      };
    });
    setParticles(seeds);
    const t = setTimeout(() => setParticles([]), 1100);
    return () => clearTimeout(t);
  }, [visible, reduced]);

  if (!visible) return null;

  return (
    <div className="c-celebration" data-testid="celebration-overlay" aria-live="polite">
      <div className="c-celebration-message">{message}</div>
      {particles.map((p) => (
        <span
          key={p.id}
          className="c-celebration-dot"
          style={{
            width: p.size,
            height: p.size,
            background: p.color,
            transform: `translate(${p.dx}px, ${p.dy}px)`,
            animationDelay: `${p.delay}ms`,
          }}
        />
      ))}
    </div>
  );
}
